import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  styled,
  keyframes
} from '@mui/material';

// Keyframe animations
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0.6);
  }
  40% {
    transform: scale(1);
  }
`;

// Styled components for animations
const SpinningBox = styled(Box)(({ theme }) => ({
  animation: `${spin} 1.2s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const BouncingDot = styled(Box)(({ delay = 0 }) => ({
  borderRadius: '50%',
  animation: `${bounce} 1.4s ease-in-out infinite both`,
  animationDelay: `${delay}ms`,
}));

export const ModernLoader = ({ 
  size = 24, 
  strokeWidth = 2.5, 
  color = "#3b82f6",
  sx = {} 
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference * 0.75;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...sx }}>
      <SpinningBox>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            opacity="0.2"
          />
          {/* Animated circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            style={{
              transformOrigin: 'center',
            }}
          />
        </svg>
      </SpinningBox>
    </Box>
  );
};

// Alternative modern loader with gradient
const GradientLoader = ({ 
  size = 24, 
  strokeWidth = 2.5,
  sx = {} 
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', ...sx }}>
      <SpinningBox>
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          <defs>
            <linearGradient id={`gradient-${size}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="1" />
              <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ec4899" stopOpacity="0.3" />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={`url(#gradient-${size})`}
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * 0.25}
            style={{
              transformOrigin: 'center',
            }}
          />
        </svg>
      </SpinningBox>
    </Box>
  );
};

// Dots loader for variety
const DotsLoader = ({ 
  size = 24, 
  color = "#3b82f6",
  sx = {} 
}) => {
  const dotSize = size / 6;

  return (
    <Box 
      sx={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: 1,
        ...sx 
      }}
    >
      <BouncingDot
        delay={0}
        sx={{
          width: dotSize,
          height: dotSize,
          backgroundColor: color,
        }}
      />
      <BouncingDot
        delay={160}
        sx={{
          width: dotSize,
          height: dotSize,
          backgroundColor: color,
        }}
      />
      <BouncingDot
        delay={320}
        sx={{
          width: dotSize,
          height: dotSize,
          backgroundColor: color,
        }}
      />
    </Box>
  );
};

// Demo component showing all loaders
const LoaderDemo = () => {
  return (
    <Box 
      sx={{ 
        p: 4, 
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        minHeight: '100vh'
      }}
    >
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        <Typography 
          variant="h3" 
          component="h2" 
          sx={{ 
            fontWeight: 'bold', 
            color: 'grey.800', 
            textAlign: 'center', 
            mb: 6 
          }}
        >
          Modern Loading Animations
        </Typography>
        
        <Grid container spacing={4}>
          {/* Basic Modern Loader */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'grey.700' }}>
                  Classic Modern
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                  <ModernLoader size={20} />
                  <ModernLoader size={28} />
                  <ModernLoader size={36} />
                </Box>
                <Paper 
                  sx={{ 
                    p: 1, 
                    backgroundColor: 'grey.50', 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.600'
                  }}
                >
                  {'<ModernLoader size={24} />'}
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Gradient Loader */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'grey.700' }}>
                  Gradient Style
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                  <GradientLoader size={20} />
                  <GradientLoader size={28} />
                  <GradientLoader size={36} />
                </Box>
                <Paper 
                  sx={{ 
                    p: 1, 
                    backgroundColor: 'grey.50', 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.600'
                  }}
                >
                  {'<GradientLoader size={24} />'}
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Dots Loader */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'grey.700' }}>
                  Bouncing Dots
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                  <DotsLoader size={20} />
                  <DotsLoader size={28} />
                  <DotsLoader size={36} />
                </Box>
                <Paper 
                  sx={{ 
                    p: 1, 
                    backgroundColor: 'grey.50', 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.600'
                  }}
                >
                  {'<DotsLoader size={24} />'}
                </Paper>
              </CardContent>
            </Card>
          </Grid>

          {/* Color Variations */}
          <Grid item xs={12} md={6}>
            <Card 
              elevation={3}
              sx={{ 
                borderRadius: 3,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'grey.700' }}>
                  Color Variations
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 3 }}>
                  <ModernLoader size={28} color="#ef4444" />
                  <ModernLoader size={28} color="#10b981" />
                  <ModernLoader size={28} color="#f59e0b" />
                </Box>
                <Paper 
                  sx={{ 
                    p: 1, 
                    backgroundColor: 'grey.50', 
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    color: 'grey.600'
                  }}
                >
                  {'<ModernLoader color="#ef4444" />'}
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Code Examples */}
        <Paper 
          elevation={4}
          sx={{ 
            mt: 6, 
            p: 3, 
            backgroundColor: 'grey.900', 
            color: 'success.main',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            borderRadius: 3,
            overflow: 'auto'
          }}
        >
          <Typography sx={{ color: 'common.white', mb: 1 }}>
            Usage Examples:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography component="div">{'// Basic usage'}</Typography>
            <Typography component="div">{'<ModernLoader />'}</Typography>
            <Typography component="div" sx={{ mt: 2 }}>{'// Customized'}</Typography>
            <Typography component="div">{'<ModernLoader size={32} color="#ef4444" strokeWidth={3} />'}</Typography>
            <Typography component="div" sx={{ mt: 2 }}>{'// Gradient version'}</Typography>
            <Typography component="div">{'<GradientLoader size={28} />'}</Typography>
            <Typography component="div" sx={{ mt: 2 }}>{'// Dots version'}</Typography>
            <Typography component="div">{'<DotsLoader size={24} color="#3b82f6" />'}</Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export { GradientLoader, DotsLoader };
export default LoaderDemo;